import { z } from "zod";
export const ProductMemberRoleSchema = z.enum(["OWNER", "MANAGER", "CONTRIBUTOR", "VIEWER"]);
export const SignupSchema = z.object({
    email: z.string().trim().email().max(320),
    password: z.string().min(15).max(256),
    displayName: z.string().trim().min(1).max(160),
    businessName: z.string().trim().min(1).max(160),
    businessSlug: z.string().trim().regex(/^[a-z0-9][a-z0-9-]{1,62}$/u),
    timezone: z.string().trim().min(1).max(80).default("UTC")
}).strict();
export const ConfirmEmailSchema = z.object({
    token: z.string().min(32).max(512)
}).strict();
export const ProductLoginSchema = z.object({
    email: z.string().trim().email().max(320),
    password: z.string().min(1).max(256),
    businessSlug: z.string().trim().regex(/^[a-z0-9][a-z0-9-]{1,62}$/u).optional()
}).strict();
export const CreateBusinessSchema = z.object({
    name: z.string().trim().min(1).max(160),
    slug: z.string().trim().regex(/^[a-z0-9][a-z0-9-]{1,62}$/u),
    timezone: z.string().trim().min(1).max(80).default("UTC")
}).strict();
export const DepartmentAccessScopeSchema = z.enum([
    "OWN_RECORDS",
    "ASSIGNED_RECORDS",
    "DEPARTMENT_RECORDS"
]);
export const DepartmentAccessLevelSchema = z.enum(["READ", "CONTRIBUTE", "MANAGE"]);
export const ProviderKindSchema = z.enum([
    "WHATSAPP_BAILEYS",
    "WHATSAPP_CLOUD",
    "X",
    "XAI_GROK",
    "RESEND"
]);
export const ProviderServiceSchema = z.enum(["MESSAGING", "SOCIAL_PUBLISHING", "CONTENT_MODEL", "EMAIL"]);
export const ProviderCapabilitySchema = z.enum([
    "MESSAGES_READ",
    "MESSAGES_SEND",
    "SOCIAL_DRAFT",
    "SOCIAL_PUBLISH",
    "EMAIL_SEND"
]);
export const ProviderConnectionStatusSchema = z.enum([
    "PENDING",
    "CONNECTED",
    "UNAVAILABLE",
    "REVOKED"
]);
export const VaultSecretReferenceSchema = z.string()
    .trim()
    .min(10)
    .max(512)
    .regex(/^vault:\/\/[a-zA-Z0-9][a-zA-Z0-9/_:.@-]*$/u);
export const RegisterProviderConnectionSchema = z.object({
    provider: ProviderKindSchema,
    externalAccountRef: z.string().trim().min(1).max(240),
    displayName: z.string().trim().min(1).max(200),
    credentialRef: VaultSecretReferenceSchema.nullable().default(null),
    scopes: z.array(z.string().trim().min(1).max(120)).max(32).default([]),
    maxRequestsPerWindow: z.number().int().min(1).max(10_000).default(30),
    windowSeconds: z.number().int().min(1).max(86_400).default(900),
    dailyCostLimitMicros: z.number().int().min(0).max(1_000_000_000).default(0),
    estimatedCostMicrosPerRequest: z.number().int().min(0).max(100_000_000).default(0)
}).strict().superRefine((connection, context) => {
    // Zero is a literal no-metered-spend ceiling, not an implicit unlimited
    // budget. A provider estimated to cost money must receive an explicit daily
    // allowance large enough for at least one request.
    if (connection.estimatedCostMicrosPerRequest > connection.dailyCostLimitMicros) {
        context.addIssue({
            code: "custom",
            path: ["estimatedCostMicrosPerRequest"],
            message: "Estimated request cost exceeds the configured daily cost limit."
        });
    }
});
export const SocialPostBodySchema = z.string().trim().min(1).max(280);
export const CreateSocialPublicationSchema = z.object({
    publishConnectionId: z.string().uuid(),
    draftConnectionId: z.string().uuid().nullable().default(null),
    body: SocialPostBodySchema,
    scheduledFor: z.iso.datetime({ offset: true }).nullable().default(null),
    reason: z.string().trim().min(1).max(500).default("Human-created social draft")
}).strict();
export const ReviseSocialPublicationSchema = z.object({
    expectedRevision: z.number().int().positive(),
    body: SocialPostBodySchema,
    scheduledFor: z.iso.datetime({ offset: true }).nullable(),
    reason: z.string().trim().min(1).max(500)
}).strict();
export const ApproveSocialPublicationSchema = z.object({
    expectedRevision: z.number().int().positive()
}).strict();
export const SocialDraftRequestSchema = z.object({
    draftConnectionId: z.string().uuid(),
    instruction: z.string().trim().min(1).max(4_000),
    maximumCharacters: z.number().int().min(1).max(280).default(280)
}).strict();
export const SocialPublicationStateSchema = z.enum([
    "DRAFT",
    "AWAITING_APPROVAL",
    "APPROVED",
    "SCHEDULED",
    "PUBLISHING",
    "PUBLISHED",
    "FAILED",
    "RECONCILIATION_REQUIRED",
    "CANCELLED"
]);
const PROVIDER_CONTRACT = {
    WHATSAPP_BAILEYS: { service: "MESSAGING", capabilities: ["MESSAGES_READ", "MESSAGES_SEND"] },
    WHATSAPP_CLOUD: { service: "MESSAGING", capabilities: ["MESSAGES_READ", "MESSAGES_SEND"] },
    X: { service: "SOCIAL_PUBLISHING", capabilities: ["SOCIAL_PUBLISH"] },
    XAI_GROK: { service: "CONTENT_MODEL", capabilities: ["SOCIAL_DRAFT"] },
    RESEND: { service: "EMAIL", capabilities: ["EMAIL_SEND"] }
};
export function providerContract(provider) {
    return PROVIDER_CONTRACT[provider];
}
//# sourceMappingURL=product-platform.js.map